import z from 'zod';

export const ProvisioningStatusSchema = z.enum([
  'not_started',
  'in_progress',
  'failed',
  'completed',
  // * A superAdmin teardown is unwinding the resources right now. Distinct from
  // * 'completed' so a poller can tell a running teardown from an idle company,
  // * and so a second teardown/retry cannot start while the first is in flight.
  'deprovisioning',
  // * Persist-mode (or future full-wipe) superAdmin teardown end-state - the
  // * company is torn down but its audit trail is kept (unlike a fresh
  // * 'not_started' company that was never provisioned).
  'deprovisioned',
  // * A teardown that could not release every resource. Deliberately not
  // * 'failed', which means a failed provision and is what retryProvisioning
  // * accepts - a retry there would re-create resources on a half-torn-down
  // * company and re-activate it.
  'deprovision_failed',
]);
export type TProvisioningStatus = z.infer<typeof ProvisioningStatusSchema>;

export const ProvisioningEnvSchema = z.enum(['stage', 'prod']);
export type TProvisioningEnv = z.infer<typeof ProvisioningEnvSchema>;

export const ProvisioningStepStatusSchema = z.enum([
  'pending',
  'done',
  'failed',
  'rolled_back',
  'rollback_failed',
  // * A teardown deliberately left this step's resource in place (a non-empty or
  // * undeterminable S3 bucket). Distinct from 'done' - which would read as still
  // * provisioned - and from 'rolled_back', which would claim the resource is gone.
  'skipped',
]);
export type TProvisioningStepStatus = z.infer<
  typeof ProvisioningStepStatusSchema
>;

export interface IProvisionStep {
  step: number;
  name?: string;
  status: TProvisioningStepStatus;
  resourceId?: string | null;
  error?: string | null;
  // * Why a 'skipped' step was left in place, so the retained resource is
  // * auditable in the DB rather than only in a log line. Internal: the UI shows
  // * a fixed label per status, not this text.
  skipReason?: string | null;
}

// * Sanitized step exposed to the frontend: the internal AWS resourceId and the
// * operator-facing skipReason are omitted (not needed by the UI).
export type TProvisionStepPublic = Omit<
  IProvisionStep,
  'resourceId' | 'skipReason'
>;

export const PROVISIONING_STEPS = {
  MONGODB: 1,
  IAM: 2,
  S3: 3,
  CLOUDWATCH: 4,
  TASK_DEFINITION: 5,
  TARGET_GROUP: 6,
  // * Route 53 + ALB rule must precede the ECS service: creating the listener
  // * rule is what associates the target group with the shared load balancer,
  // * and AWS rejects an ECS service whose target group has no associated LB.
  ROUTE53_AND_ALB_RULE: 7,
  ECS_SERVICE: 8,
} as const;

export type TProvisioningStepNumber =
  (typeof PROVISIONING_STEPS)[keyof typeof PROVISIONING_STEPS];

// * Ordered catalog driving the frontend curtain: each entry's `key` maps to an
// * i18n label (provisioning.step.<key>). The post-pipeline proxy mapping is a
// * success-path action, not a pipeline step, so it is not listed here.
export const PROVISIONING_STEP_CATALOG = [
  { step: PROVISIONING_STEPS.MONGODB, key: 'mongodb' },
  { step: PROVISIONING_STEPS.IAM, key: 'iam' },
  { step: PROVISIONING_STEPS.S3, key: 's3' },
  { step: PROVISIONING_STEPS.CLOUDWATCH, key: 'cloudwatch' },
  { step: PROVISIONING_STEPS.TASK_DEFINITION, key: 'task-definition' },
  { step: PROVISIONING_STEPS.TARGET_GROUP, key: 'target-group' },
  { step: PROVISIONING_STEPS.ROUTE53_AND_ALB_RULE, key: 'route53-alb' },
  { step: PROVISIONING_STEPS.ECS_SERVICE, key: 'ecs-service' },
] as const;

// * Synthetic step number for the post-pipeline proxy mapping. It never appears
// * in the persisted steps[]; the curtain renders a row for it only when a failed
// * event/status names it (one past the last pipeline step).
export const PROVISIONING_PROXY_MAPPING_STEP =
  PROVISIONING_STEPS.ECS_SERVICE + 1;
